import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import * as Icons from "lucide-react";
import { SlidersHorizontal, Search as SearchIcon } from "lucide-react";
import api from "@/lib/api";
import { ProjectCard, ProjectCardSkeleton } from "@/components/ProjectCard";
import { EmptyState } from "@/components/Loader";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const SORTS = [
  { v: "relevance", l: "Relevância" },
  { v: "recent", l: "Mais recentes" },
  { v: "popular", l: "Mais populares" },
  { v: "likes", l: "Mais curtidos" },
  { v: "saves", l: "Mais salvos" },
];

const DIFFS = [
  { v: "", l: "Todas dificuldades" },
  { v: "facil", l: "Fácil" },
  { v: "medio", l: "Médio" },
  { v: "dificil", l: "Difícil" },
];

const COSTS = [
  { v: "", l: "Qualquer custo" },
  { v: "0-100", l: "Até R$ 100" },
  { v: "100-300", l: "R$ 100 – 300" },
  { v: "300-600", l: "R$ 300 – 600" },
  { v: "600-", l: "Acima de R$ 600" },
];

export default function Explore() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(searchParams.get("q") || "");

  const category = searchParams.get("category") || "";
  const difficulty = searchParams.get("difficulty") || "";
  const cost = searchParams.get("cost") || "";
  const sort = searchParams.get("sort") || "relevance";
  const page = parseInt(searchParams.get("page") || "1", 10);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  const setParam = useCallback((key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== "page") next.delete("page");
    setSearchParams(next);
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    setLoading(true);
    const params = { sort, page, limit: 12 };
    if (searchParams.get("q")) params.q = searchParams.get("q");
    if (category) params.category = category;
    if (difficulty) params.difficulty = difficulty;
    if (cost) {
      const [min, max] = cost.split("-");
      if (min) params.min_cost = min;
      if (max) params.max_cost = max;
    }
    api.get("/projects", { params })
      .then((r) => setData(r.data))
      .catch(() => setData({ items: [], total: 0, pages: 0 }))
      .finally(() => setLoading(false));
  }, [searchParams, category, difficulty, cost, sort, page]);

  const submitSearch = (e) => {
    e.preventDefault();
    setParam("q", q.trim());
  };

  const clearFilters = () => { setQ(""); setSearchParams(new URLSearchParams()); };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <p className="eyebrow text-primary">Biblioteca da comunidade</p>
        <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold tracking-tight">Explorar projetos</h1>
        <p className="mt-2 text-muted-foreground">
          {data ? `${data.total} projeto(s) encontrado(s)` : "Carregando..."}
        </p>
      </div>

      <form onSubmit={submitSearch} className="mb-5">
        <div className="relative max-w-xl">
          <SearchIcon className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            data-testid="explore-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nome, material, tipo de móvel..."
            className="h-12 w-full rounded-full border border-input bg-card pl-11 pr-28 text-sm outline-none focus:border-primary"
          />
          <Button type="submit" data-testid="explore-search-btn" className="absolute right-1.5 top-1.5 h-9 rounded-full">Buscar</Button>
        </div>
      </form>

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <span className="flex items-center gap-1.5 text-sm font-semibold text-muted-foreground">
          <SlidersHorizontal className="h-4 w-4" /> Filtros:
        </span>
        <FilterSelect testid="filter-category" value={category} onChange={(v) => setParam("category", v)} placeholder="Todas categorias"
          options={[{ v: "", l: "Todas categorias" }, ...categories.map((c) => ({ v: c.slug, l: c.name }))]} />
        <FilterSelect testid="filter-difficulty" value={difficulty} onChange={(v) => setParam("difficulty", v)} placeholder="Dificuldade" options={DIFFS} />
        <FilterSelect testid="filter-cost" value={cost} onChange={(v) => setParam("cost", v)} placeholder="Custo" options={COSTS} />
        <div className="ml-auto flex items-center gap-2">
          <span className="text-sm text-muted-foreground hidden sm:inline">Ordenar:</span>
          <FilterSelect testid="filter-sort" value={sort} onChange={(v) => setParam("sort", v)} placeholder="Ordenar" options={SORTS.map((s) => ({ v: s.v, l: s.l }))} />
        </div>
        {(category || difficulty || cost || searchParams.get("q")) && (
          <Button variant="ghost" size="sm" data-testid="clear-filters-btn" onClick={clearFilters} className="text-destructive">Limpar</Button>
        )}
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, i) => <ProjectCardSkeleton key={i} />)}
        </div>
      ) : data?.items?.length ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {data.items.map((p, i) => <ProjectCard key={p.id} project={p} index={i} />)}
          </div>
          {data.pages > 1 && (
            <div className="mt-10 flex items-center justify-center gap-2">
              <Button variant="outline" disabled={page <= 1} onClick={() => setParam("page", String(page - 1))} data-testid="page-prev">Anterior</Button>
              <span className="px-4 text-sm font-medium">{page} / {data.pages}</span>
              <Button variant="outline" disabled={page >= data.pages} onClick={() => setParam("page", String(page + 1))} data-testid="page-next">Próxima</Button>
            </div>
          )}
        </>
      ) : (
        <EmptyState title="Nenhum projeto encontrado." subtitle="Tente ajustar os filtros ou buscar por outro termo."
          action={<Button onClick={clearFilters} className="rounded-full">Limpar filtros</Button>} />
      )}
    </div>
  );
}

function FilterSelect({ value, onChange, options, placeholder, testid }) {
  return (
    <Select value={value || "all__"} onValueChange={(v) => onChange(v === "all__" ? "" : v)}>
      <SelectTrigger data-testid={testid} className="h-9 w-auto min-w-[150px] rounded-full bg-card text-sm">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o.v || "all__"} value={o.v || "all__"}>{o.l}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
